import { Address } from '@/types/address'
import { computed } from 'vue'

/**
 * Base checkout store composable providing shared getters and actions
 * Can be used by regular, guest, and OPC checkout stores
 */
export function useBaseCheckoutStore(store: {
  selectedShippingAddressId?: number | null
  selectedShippingAddressData?: Address | null
  selectedBillingAddressId?: number | null
  selectedBillingAddressData?: Address | null
  selectedPaymentMethodId?: number | null
  selectedPaymentMethodData?: object | null
  useSameBillingAddress?: boolean | null
  email?: string | null
  phoneNumber?: string | null
  ageData?: any
  subscribeToNewsletter?: boolean | null
  doiOptIn?: boolean | null
  selectedDeliveryOptionsMap?: object | null
  blacklistedPaymentMethodIds?: Array<number>
  blacklistedPaymentMethodCodes?: Array<string>
}) {
  /**
   * Check if shipping address is selected
   */
  const hasShippingAddress = computed(() => {
    return !!(
      store.selectedShippingAddressId || store.selectedShippingAddressData
    )
  })

  /**
   * Check if billing address is selected
   */
  const hasBillingAddress = computed(() => {
    return !!(
      store.selectedBillingAddressId ||
      store.selectedBillingAddressData ||
      store.useSameBillingAddress
    )
  })

  /**
   * Check if payment method is selected
   */
  const hasPaymentMethod = computed(() => {
    return !!(store.selectedPaymentMethodId || store.selectedPaymentMethodData)
  })

  /**
   * Check if email is provided
   */
  const hasEmail = computed(() => {
    return !!store.email
  })

  /**
   * Clear all checkout data
   */
  const clearData = () => {
    if ('useSameBillingAddress' in store) {
      store.useSameBillingAddress = null
    }
    if ('selectedPaymentMethodData' in store) {
      store.selectedPaymentMethodData = null
    }
    if ('selectedPaymentMethodId' in store) {
      store.selectedPaymentMethodId = null
    }
    if ('selectedDeliveryOptionsMap' in store) {
      store.selectedDeliveryOptionsMap = null
    }
    if ('selectedShippingAddressData' in store) {
      store.selectedShippingAddressData = null
    }
    if ('selectedShippingAddressId' in store) {
      store.selectedShippingAddressId = null
    }
    if ('selectedBillingAddressData' in store) {
      store.selectedBillingAddressData = null
    }
    if ('selectedBillingAddressId' in store) {
      store.selectedBillingAddressId = null
    }
    if ('email' in store) {
      store.email = null
    }
    if ('subscribeToNewsletter' in store) {
      store.subscribeToNewsletter = null
    }
    if ('doiOptIn' in store) {
      store.doiOptIn = null
    }
    if ('phoneNumber' in store) {
      store.phoneNumber = null
    }
    if ('ageData' in store) {
      store.ageData = null
    }
    if ('blacklistedPaymentMethodIds' in store) {
      store.blacklistedPaymentMethodIds = []
    }
    if ('blacklistedPaymentMethodCodes' in store) {
      store.blacklistedPaymentMethodCodes = []
    }
  }

  return {
    hasShippingAddress,
    hasBillingAddress,
    hasPaymentMethod,
    hasEmail,
    clearData
  }
}
